import type { AppState } from '@app-types/domain';
import { INITIAL_STATE } from '@app-types/domain';

const STORAGE_KEY = 'robox_app_state_v1';
const SCHEMA_VERSION = 1;

interface Envelope {
  version: number;
  state: AppState;
  saved_at: string;
}

export function loadState(): AppState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return INITIAL_STATE;
    const env: Envelope = JSON.parse(raw);
    if (env.version !== SCHEMA_VERSION) {
      // Migração futura entra aqui
      console.warn(`Schema version mismatch: ${env.version} → ${SCHEMA_VERSION}. Using initial.`);
      return INITIAL_STATE;
    }
    // Merge com defaults para garantir novos campos
    return {
      ...INITIAL_STATE,
      ...env.state,
      settings: { ...INITIAL_STATE.settings, ...env.state.settings },
    };
  } catch (err) {
    console.error('Failed to load state:', err);
    return INITIAL_STATE;
  }
}

export function saveState(state: AppState): void {
  try {
    const env: Envelope = {
      version: SCHEMA_VERSION,
      state,
      saved_at: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(env));
  } catch (err) {
    console.error('Failed to save state:', err);
  }
}

export function exportStateJSON(state: AppState): string {
  return JSON.stringify(
    { version: SCHEMA_VERSION, state, exported_at: new Date().toISOString() },
    null,
    2,
  );
}

export function clearAllState(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/** timestamp do último save local (para decisão last-write-wins no sync) */
export function loadSavedAt(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return (JSON.parse(raw) as Envelope).saved_at ?? null;
  } catch {
    return null;
  }
}

export type ImportResult =
  | { ok: true; contents: number; sessions: number; goals: number }
  | { ok: false; error: string };

/** importa um backup exportado (aceita envelope {version,state} ou state cru) */
export function importStateJSON(json: string): ImportResult {
  try {
    const parsed = JSON.parse(json) as { state?: AppState } | AppState;
    const state = ('state' in parsed && parsed.state ? parsed.state : parsed) as AppState;
    if (!state || typeof state !== 'object') return { ok: false, error: 'JSON inválido' };
    if (!Array.isArray(state.contents) || !Array.isArray(state.sessions)) {
      return {
        ok: false,
        error: 'Não parece um backup do Robox (faltam contents/sessions)',
      };
    }
    const merged: AppState = {
      ...INITIAL_STATE,
      ...state,
      settings: { ...INITIAL_STATE.settings, ...(state.settings || {}) },
    };
    saveState(merged);
    return {
      ok: true,
      contents: merged.contents.length,
      sessions: merged.sessions.length,
      goals: merged.goals.length,
    };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'Falha ao ler JSON' };
  }
}
